import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { ArrowSquareOut, BookOpen, Calendar, Hash, Quotes } from "@phosphor-icons/react";
import { fetchPaperDetail } from "@/lib/api";

const COLORS = ["#B4533A", "#E8A33D", "#2C5F5C", "#2E3A87"];

function MetaPill({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={`paper-meta-${label.toLowerCase()}`}>
      <Icon size={14} weight="duotone" className="text-terracotta mt-0.5 shrink-0" />
      <div>
        <div className="overline">{label}</div>
        <div className="text-ink mt-0.5">{value}</div>
      </div>
    </div>
  );
}

export const PublicationDetailDialog = ({ paperId, fallback, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !paperId) return;
    let cancelled = false;
    setData(null);
    setError(null);
    setLoading(true);
    fetchPaperDetail(paperId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, paperId]);

  const title = data?.title || fallback?.title || "";
  const venue = data?.journal || data?.book || data?.conference || fallback?.venue || "";
  const yearly = data?.citations_per_year || [];
  const max = yearly.length ? Math.max(...yearly.map((d) => d.count)) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl bg-white border border-ink/15 rounded-none p-0 max-h-[88vh] overflow-y-auto"
        data-testid="publication-detail-dialog"
      >
        <DialogHeader className="p-6 pb-4 border-b border-paper-edge">
          <div className="overline text-terracotta mb-2">Publication detail</div>
          <DialogTitle className="font-display text-3xl tracking-tight leading-tight pr-6">
            {title}
          </DialogTitle>
          {venue && (
            <DialogDescription className="text-ink-mute italic mt-2">{venue}</DialogDescription>
          )}
        </DialogHeader>

        {loading && (
          <div className="p-8 space-y-3 animate-pulse" data-testid="paper-loading">
            <div className="h-3 bg-paper-alt w-2/3" />
            <div className="h-3 bg-paper-alt w-1/2" />
            <div className="h-40 bg-paper-alt w-full mt-6" />
          </div>
        )}

        {error && (
          <div className="p-8 text-sm font-mono text-terracotta" data-testid="paper-error">
            {error}
          </div>
        )}

        {data && (
          <div className="p-6 lg:p-8 space-y-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <MetaPill icon={Calendar} label="Published" value={data.publication_date} />
              <MetaPill icon={Hash} label="Volume / Issue" value={[data.volume, data.issue].filter(Boolean).join(" · ")} />
              <MetaPill icon={Hash} label="Pages" value={data.pages} />
              <MetaPill icon={BookOpen} label="Publisher" value={data.publisher} />
              <MetaPill icon={Quotes} label="Total citations" value={String(data.total_citations || 0)} />
              <MetaPill icon={BookOpen} label="Authors" value={data.authors} />
            </div>

            {data.description && (
              <div data-testid="paper-abstract">
                <div className="overline mb-2">Abstract</div>
                <p className="font-display text-lg leading-relaxed text-ink whitespace-pre-line">
                  {data.description}
                </p>
              </div>
            )}

            {yearly.length > 0 && (
              <div data-testid="paper-yearly">
                <div className="overline mb-3">Citations per year</div>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={yearly} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                      <XAxis
                        dataKey="year"
                        tick={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11, fill: "#666" }}
                        tickLine={false}
                        axisLine={{ stroke: "#E5E5E0" }}
                      />
                      <YAxis
                        tick={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11, fill: "#666" }}
                        tickLine={false}
                        axisLine={false}
                        width={28}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(180,83,58,0.08)" }}
                        contentStyle={{
                          background: "white",
                          border: "1px solid #1a1a1a30",
                          borderRadius: 0,
                          fontFamily: "IBM Plex Mono, monospace",
                          fontSize: 11,
                        }}
                      />
                      <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                        {yearly.map((entry, idx) => (
                          <Cell
                            key={entry.year}
                            fill={entry.count === max ? "#B4533A" : COLORS[idx % COLORS.length]}
                            fillOpacity={entry.count === max ? 1 : 0.78}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              {data.scholar_url && (
                <a
                  href={data.scholar_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-xs"
                  data-testid="paper-scholar-link"
                >
                  Open on Scholar <ArrowSquareOut size={13} weight="bold" />
                </a>
              )}
              {data.article_url && (
                <a
                  href={data.article_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-xs"
                  data-testid="paper-article-link"
                >
                  Read article <ArrowSquareOut size={13} weight="bold" />
                </a>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PublicationDetailDialog;
